// Account Details — post-OTP identity completion.
//
// Rules:
//   * Phone is authoritative from the Supabase session (read-only).
//   * first_name / last_name / email are captured here and persisted
//     via update_customer_profile. Supabase is authoritative for
//     uniqueness and format; React only checks the empty/format
//     boundary to reduce round-trips.
//   * Strict block: no "skip" affordance. Customers must complete
//     these fields before proceeding to any protected route (review,
//     order, meals).
//   * returnTo is preserved via the `?returnTo=` query param and
//     handed back after a successful save. Only internal paths are
//     honoured — no protocol-relative or external redirects.

import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useLocation, Navigate } from 'react-router-dom';
import { AppShell } from '../components/AppShell';
import { BrandButton } from '../components/BrandButton';
import { useSession } from '../lib/useSession';
import {
  rpcGetCustomerProfile,
  rpcUpdateCustomerProfile,
  readProfilePhone,
  readProfileFirstName,
  readProfileLastName,
  readProfileEmail,
  readProfileOutcome,
  updateOutcomeToInlineError,
  isUpdateSuccessOutcome,
  isValidEmailFormat,
} from '../lib/customerProfile';

function sanitizeReturnTo(candidate) {
  if (typeof candidate !== 'string') return null;
  if (!candidate.startsWith('/')) return null;
  if (candidate.startsWith('//')) return null;         // protocol-relative
  if (candidate.startsWith('/sign-in')) return null;    // no auth loop
  if (candidate.startsWith('/account/details')) return null; // no self-loop
  return candidate;
}

function readReturnToFromLocation(location) {
  try {
    const sp = new URLSearchParams(location.search || '');
    return sanitizeReturnTo(sp.get('returnTo'));
  } catch {
    return null;
  }
}

export default function AccountDetails() {
  const nav = useNavigate();
  const location = useLocation();
  const { session, loading: sessionLoading, configured } = useSession();

  const returnTo = useMemo(() => readReturnToFromLocation(location), [location]);

  // Hydrated from get_customer_profile.
  const [hydrating, setHydrating] = useState(true);
  const [hydrateError, setHydrateError] = useState('');
  const [phone, setPhone] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');

  // Save state.
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState('');
  const [fieldError, setFieldError] = useState({ firstName: '', lastName: '', email: '' });

  // Load profile once we have a session.
  useEffect(() => {
    if (sessionLoading) return;
    if (!session) return; // <Navigate> renders below
    let cancelled = false;
    setHydrating(true);
    setHydrateError('');
    rpcGetCustomerProfile().then((res) => {
      if (cancelled) return;
      if (!res.ok) {
        setHydrateError(res.error || 'We could not load your account details.');
        setHydrating(false);
        return;
      }
      const payload = res.data;
      const p = readProfilePhone(payload) || session?.user?.phone || '';
      setPhone(p ? (p.startsWith('+') ? p : '+' + p) : '');
      setFirstName(readProfileFirstName(payload) || '');
      setLastName(readProfileLastName(payload) || '');
      setEmail(readProfileEmail(payload) || '');
      setHydrating(false);
    });
    return () => { cancelled = true; };
  }, [sessionLoading, session]);

  // Auth guard — bounce to sign-in preserving the requested returnTo
  // so the customer lands back on the correct downstream page.
  if (!sessionLoading && !session) {
    const nextReturnTo = returnTo ? `/account/details?returnTo=${encodeURIComponent(returnTo)}` : '/account/details';
    return <Navigate to="/sign-in" replace state={{ returnTo: nextReturnTo }} />;
  }

  function validateBeforeSubmit() {
    const errs = { firstName: '', lastName: '', email: '' };
    const fn = firstName.trim();
    const ln = lastName.trim();
    const em = email.trim();
    if (fn.length === 0) errs.firstName = 'Please enter your first name.';
    else if (fn.length > 60) errs.firstName = 'First name is too long.';
    if (ln.length === 0) errs.lastName = 'Please enter your last name.';
    else if (ln.length > 60) errs.lastName = 'Last name is too long.';
    if (em.length === 0) errs.email = 'Please enter your email address.';
    else if (!isValidEmailFormat(em)) errs.email = 'That email address does not look valid.';
    setFieldError(errs);
    return !errs.firstName && !errs.lastName && !errs.email;
  }

  async function handleSubmit(e) {
    e?.preventDefault?.();
    setFormError('');
    if (!validateBeforeSubmit()) return;
    setSaving(true);
    const res = await rpcUpdateCustomerProfile({
      firstName: firstName.trim(),
      lastName: lastName.trim(),
      email: email.trim(),
    });
    setSaving(false);
    if (!res.ok) {
      setFormError(res.error || 'We could not save your details. Please try again.');
      return;
    }
    const outcome = readProfileOutcome(res.data);
    if (isUpdateSuccessOutcome(outcome)) {
      const to = returnTo || '/';
      nav(to, { replace: true });
      return;
    }
    // Backend rejected — map the outcome to inline copy.
    const mapped = updateOutcomeToInlineError(outcome);
    if (outcome === 'email_taken' || outcome === 'email_in_use' || outcome === 'duplicate_email' || outcome === 'invalid_email' || outcome === 'email_invalid') {
      setFieldError((prev) => ({ ...prev, email: mapped || 'Please use a different email.' }));
    } else if (outcome === 'invalid_first_name' || outcome === 'first_name_invalid') {
      setFieldError((prev) => ({ ...prev, firstName: mapped || 'Please enter a valid first name.' }));
    } else if (outcome === 'invalid_last_name' || outcome === 'last_name_invalid') {
      setFieldError((prev) => ({ ...prev, lastName: mapped || 'Please enter a valid last name.' }));
    } else {
      setFormError(mapped || 'We could not save your details. Please try again.');
    }
  }

  const inputStyle = {
    padding: '14px 16px',
    border: '1px solid var(--line)',
    borderRadius: 'var(--radius)',
    background: 'var(--paper)',
    color: 'var(--ink)',
    fontSize: '16px',
    width: '100%',
  };
  const errStyle = { color: 'var(--danger, #b03a2e)' };

  return (
    <AppShell>
      <div className="wizard-card">
        <div className="eyebrow" data-testid="account-details-eyebrow">Account</div>
        <h1 className="h2 mt-3" data-testid="account-details-title">
          A couple of details before we continue
        </h1>
        <p className="mt-3" style={{ color: 'var(--text-dim)' }} data-testid="account-details-subtitle">
          We need your name and email to prepare your order and pickup. Your phone
          number is already on file from sign-in.
        </p>

        {!configured && (
          <div
            className="mt-6 p-4"
            style={{
              background: 'var(--warning-bg)',
              border: '1px solid var(--warning)',
              borderRadius: 'var(--radius)',
            }}
            data-testid="account-details-not-configured"
          >
            <p className="text-sm" style={{ color: 'var(--ink-2)' }}>
              Your account is temporarily unavailable. Please try again later.
            </p>
          </div>
        )}

        {(sessionLoading || hydrating) && configured && (
          <div className="mt-8" data-testid="account-details-loading">
            <p className="text-sm" style={{ color: 'var(--text-dim)' }}>
              Loading your account…
            </p>
          </div>
        )}

        {hydrateError && !hydrating && (
          <div
            className="mt-6 p-4"
            style={{ background: 'var(--warning-bg)', border: '1px solid var(--warning)', borderRadius: 'var(--radius)' }}
            data-testid="account-details-hydrate-error"
          >
            <p className="text-sm" style={errStyle}>{hydrateError}</p>
            <div className="mt-4">
              <BrandButton
                type="button"
                variant="outline"
                onClick={() => window.location.reload()}
                data-testid="account-details-retry-btn"
              >
                Try again
              </BrandButton>
            </div>
          </div>
        )}

        {!hydrating && !hydrateError && configured && session && (
          <form onSubmit={handleSubmit} className="mt-8" data-testid="account-details-form" noValidate>
            {/* Phone (read-only) */}
            <label htmlFor="acct-phone" className="eyebrow inline-block" style={{ color: 'var(--ink)' }}>
              Phone on file
            </label>
            <input
              id="acct-phone"
              type="tel"
              value={phone || '—'}
              readOnly
              className="mt-2"
              style={{ ...inputStyle, background: 'var(--paper-2, rgba(0,0,0,0.03))', color: 'var(--ink-2)' }}
              data-testid="account-details-phone-input"
            />

            {/* First name */}
            <label htmlFor="acct-first" className="eyebrow mt-6 inline-block" style={{ color: 'var(--ink)' }}>
              First name
            </label>
            <input
              id="acct-first"
              type="text"
              autoComplete="given-name"
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              disabled={saving}
              maxLength={60}
              className="mt-2"
              style={inputStyle}
              data-testid="account-details-first-name-input"
            />
            {fieldError.firstName && (
              <p className="mt-2 text-sm" style={errStyle} data-testid="account-details-first-name-error">
                {fieldError.firstName}
              </p>
            )}

            {/* Last name */}
            <label htmlFor="acct-last" className="eyebrow mt-6 inline-block" style={{ color: 'var(--ink)' }}>
              Last name
            </label>
            <input
              id="acct-last"
              type="text"
              autoComplete="family-name"
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
              disabled={saving}
              maxLength={60}
              className="mt-2"
              style={inputStyle}
              data-testid="account-details-last-name-input"
            />
            {fieldError.lastName && (
              <p className="mt-2 text-sm" style={errStyle} data-testid="account-details-last-name-error">
                {fieldError.lastName}
              </p>
            )}

            {/* Email */}
            <label htmlFor="acct-email" className="eyebrow mt-6 inline-block" style={{ color: 'var(--ink)' }}>
              Email address
            </label>
            <input
              id="acct-email"
              type="email"
              autoComplete="email"
              inputMode="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              disabled={saving}
              maxLength={254}
              className="mt-2"
              style={inputStyle}
              data-testid="account-details-email-input"
            />
            {fieldError.email && (
              <p className="mt-2 text-sm" style={errStyle} data-testid="account-details-email-error">
                {fieldError.email}
              </p>
            )}

            {formError && (
              <p className="mt-6 text-sm" style={errStyle} data-testid="account-details-form-error">
                {formError}
              </p>
            )}

            <div className="mt-8 flex items-center gap-3 flex-wrap">
              <BrandButton
                type="submit"
                variant="primary"
                disabled={saving}
                data-testid="account-details-save-btn"
              >
                {saving ? 'Saving…' : 'Save and continue'}
              </BrandButton>
            </div>

            <p className="mt-6 text-sm" style={{ color: 'var(--text-dim)' }} data-testid="account-details-return-hint">
              {returnTo
                ? 'You’ll return to where you left off after saving.'
                : 'You’ll continue to your account after saving.'}
            </p>
          </form>
        )}
      </div>
    </AppShell>
  );
}
